import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  User,
  DollarSign,
  Calendar,
  FileText,
  Eye,
  AlertTriangle,
  Plus,
  Minus,
  Edit,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import type { CompensationRule } from '@/services/compensation/types';

interface PendingChange {
  id: string;
  type: 'create' | 'update' | 'delete';
  rule: CompensationRule;
  originalRule?: CompensationRule;
  submittedBy: {
    id: string;
    name: string;
    role: string;
  };
  submittedAt: string;
  status: 'pending' | 'approved' | 'rejected';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  reason?: string;
  impactEstimate?: {
    total_cost_change: number;
    affected_employee_count: number;
    estimated_gross_change: number;
    comparison_period: string;
  };
}

interface ApprovalWorkflowCardProps {
  change: PendingChange;
  isSelected: boolean;
  onSelect: (selected: boolean) => void;
  onApprove: () => void;
  onReject: () => void;
  onView: () => void;
  isLoading: boolean;
  readonly?: boolean;
}

export function ApprovalWorkflowCard({
  change,
  isSelected,
  onSelect,
  onApprove,
  onReject,
  onView,
  isLoading,
  readonly = false
}: ApprovalWorkflowCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { currencyCode } = useWorkplaceCurrency();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  const getStatusIcon = (status: PendingChange['status']) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-orange-600" />;
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-600" />;
    }
  };

  const getStatusColor = (status: PendingChange['status']) => {
    switch (status) {
      case 'pending':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'approved':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'rejected':
        return 'text-red-600 bg-red-50 border-red-200';
    }
  };

  const getPriorityColor = (priority: PendingChange['priority']) => {
    switch (priority) {
      case 'urgent':
        return 'destructive';
      case 'high':
        return 'secondary';
      case 'medium':
        return 'outline';
      case 'low':
        return 'outline';
    }
  };

  const getChangeTypeIcon = (type: PendingChange['type']) => {
    switch (type) {
      case 'create':
        return <Plus className="h-4 w-4 text-green-600" />;
      case 'update':
        return <Edit className="h-4 w-4 text-blue-600" />;
      case 'delete':
        return <Minus className="h-4 w-4 text-red-600" />;
    }
  };

  const getChangeTypeLabel = (type: PendingChange['type']) => {
    switch (type) {
      case 'create':
        return 'New Rule';
      case 'update':
        return 'Update Rule';
      case 'delete':
        return 'Delete Rule';
    }
  };

  const renderRuleChanges = () => {
    const { rule, originalRule, type } = change;

    if (type === 'create') {
      return (
        <div className="space-y-3">
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <h4 className="font-medium text-green-800 mb-2">New Rule Details</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="font-medium">Rule Type:</span>
                <span className="ml-2">{rule.rule_type}</span>
              </div>
              <div>
                <span className="font-medium">Rule Name:</span>
                <span className="ml-2">{rule.rule_name}</span>
              </div>
              {rule.base_rate && (
                <div>
                  <span className="font-medium">Base Rate:</span>
                  <span className="ml-2">{formatCurrency(rule.base_rate)}</span>
                </div>
              )}
              {rule.percentage_rate && (
                <div>
                  <span className="font-medium">Percentage:</span>
                  <span className="ml-2">{rule.percentage_rate}%</span>
                </div>
              )}
              {rule.multiplier_rate && (
                <div>
                  <span className="font-medium">Multiplier:</span>
                  <span className="ml-2">{rule.multiplier_rate}x</span>
                </div>
              )}
              <div>
                <span className="font-medium">Effective From:</span>
                <span className="ml-2">{format(new Date(rule.effective_from), 'MMM dd, yyyy')}</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (type === 'update' && originalRule) {
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Original Values */}
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <h4 className="font-medium text-red-800 mb-2">Current Values</h4>
              <div className="space-y-2 text-sm">
                {originalRule.base_rate && (
                  <div className="flex justify-between">
                    <span>Base Rate:</span>
                    <span>{formatCurrency(originalRule.base_rate)}</span>
                  </div>
                )}
                {originalRule.percentage_rate && (
                  <div className="flex justify-between">
                    <span>Percentage:</span>
                    <span>{originalRule.percentage_rate}%</span>
                  </div>
                )}
                {originalRule.multiplier_rate && (
                  <div className="flex justify-between">
                    <span>Multiplier:</span>
                    <span>{originalRule.multiplier_rate}x</span>
                  </div>
                )}
              </div>
            </div>

            {/* New Values */}
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="font-medium text-green-800 mb-2">Proposed Values</h4>
              <div className="space-y-2 text-sm">
                {rule.base_rate && (
                  <div className="flex justify-between">
                    <span>Base Rate:</span>
                    <span className="flex items-center gap-2">
                      {formatCurrency(rule.base_rate)}
                      {originalRule.base_rate && rule.base_rate !== originalRule.base_rate && (
                        <Badge variant={rule.base_rate > originalRule.base_rate ? 'default' : 'secondary'} className="text-xs">
                          {rule.base_rate > originalRule.base_rate ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                        </Badge>
                      )}
                    </span>
                  </div>
                )}
                {rule.percentage_rate && (
                  <div className="flex justify-between">
                    <span>Percentage:</span>
                    <span className="flex items-center gap-2">
                      {rule.percentage_rate}%
                      {originalRule.percentage_rate && rule.percentage_rate !== originalRule.percentage_rate && (
                        <Badge variant={rule.percentage_rate > originalRule.percentage_rate ? 'default' : 'secondary'} className="text-xs">
                          {rule.percentage_rate > originalRule.percentage_rate ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                        </Badge>
                      )}
                    </span>
                  </div>
                )}
                {rule.multiplier_rate && (
                  <div className="flex justify-between">
                    <span>Multiplier:</span>
                    <span className="flex items-center gap-2">
                      {rule.multiplier_rate}x
                      {originalRule.multiplier_rate && rule.multiplier_rate !== originalRule.multiplier_rate && (
                        <Badge variant={rule.multiplier_rate > originalRule.multiplier_rate ? 'default' : 'secondary'} className="text-xs">
                          {rule.multiplier_rate > originalRule.multiplier_rate ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                        </Badge>
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (type === 'delete') {
      return (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <h4 className="font-medium text-red-800 mb-2">Rule to be Deleted</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="font-medium">Rule Name:</span>
              <span className="ml-2">{rule.rule_name}</span>
            </div>
            <div>
              <span className="font-medium">Rule Type:</span>
              <span className="ml-2">{rule.rule_type}</span>
            </div>
            {rule.base_rate && (
              <div>
                <span className="font-medium">Base Rate:</span>
                <span className="ml-2">{formatCurrency(rule.base_rate)}</span>
              </div>
            )}
            <div>
              <span className="font-medium">Effective From:</span>
              <span className="ml-2">{format(new Date(rule.effective_from), 'MMM dd, yyyy')}</span>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <Card className={cn(
      "transition-all duration-200 hover:shadow-md",
      isSelected && "ring-2 ring-blue-500 ring-opacity-50",
      readonly && "opacity-75"
    )}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            {/* Selection Checkbox (only for pending) */}
            {!readonly && change.status === 'pending' && (
              <Checkbox
                checked={isSelected}
                onCheckedChange={onSelect}
                className="mt-1"
              />
            )}

            {/* Change Type Icon */}
            <div className="flex-shrink-0">
              {getChangeTypeIcon(change.type)}
            </div>

            {/* Change Info */}
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-base">{change.rule.rule_name}</CardTitle>
                <Badge variant={getPriorityColor(change.priority)}>
                  {change.priority}
                </Badge>
                <Badge variant="outline">{getChangeTypeLabel(change.type)}</Badge>
              </div>
              
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>Rule Type: {change.rule.rule_type}</span>
                <span>•</span>
                <span>Rule: {change.rule.rule_name}</span>
                <span>•</span>
                <span>Submitted {formatDistanceToNow(new Date(change.submittedAt))} ago</span>
              </div>

              {/* Submitter Info */}
              <div className="flex items-center gap-2">
                <Avatar className="h-6 w-6">
                  <AvatarFallback className="text-xs">
                    {change.submittedBy.name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm text-muted-foreground">
                  by {change.submittedBy.name} ({change.submittedBy.role})
                </span>
              </div>
            </div>
          </div>

          {/* Status and Actions */}
          <div className="flex items-center gap-2">
            <div className={cn("flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium", getStatusColor(change.status))}>
              {getStatusIcon(change.status)}
              <span className="capitalize">{change.status}</span>
            </div>

            <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm">
                  {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </CollapsibleTrigger>
            </Collapsible>
          </div>
        </div>
      </CardHeader>

      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-4">
            <Separator />

            {/* Rule Changes Details */}
            {renderRuleChanges()}

            {/* Impact Estimate */}
            {change.impactEstimate && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">Estimated Impact</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <span className="font-medium">Cost Change:</span>
                    <span className={cn(
                      "ml-2 font-bold",
                      change.impactEstimate.total_cost_change >= 0 ? "text-red-600" : "text-green-600"
                    )}>
                      {change.impactEstimate.total_cost_change >= 0 ? '+' : ''}
                      {formatCurrency(change.impactEstimate.total_cost_change)}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium">Affected:</span>
                    <span className="ml-2">{change.impactEstimate.affected_employee_count} employee(s)</span>
                  </div>
                  <div>
                    <span className="font-medium">Period:</span>
                    <span className="ml-2 capitalize">{change.impactEstimate.comparison_period}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Reason (if provided) */}
            {change.reason && (
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-medium text-gray-800 mb-2">Reason</h4>
                <p className="text-sm text-gray-700">{change.reason}</p>
              </div>
            )}

            {/* Actions */}
            {!readonly && change.status === 'pending' && (
              <div className="flex items-center justify-between pt-3 border-t">
                <Button variant="ghost" size="sm" onClick={onView}>
                  <Eye className="h-4 w-4 mr-2" />
                  View Details
                </Button>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onReject}
                    disabled={isLoading}
                    className="text-red-600 hover:text-red-700"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    onClick={onApprove}
                    disabled={isLoading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                </div>
              </div>
            )}

            {/* Read-only status info */}
            {readonly && change.status !== 'pending' && (
              <div className="flex items-center justify-between pt-3 border-t text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  {getStatusIcon(change.status)}
                  <span>
                    {change.status === 'approved' ? 'Approved' : 'Rejected'} on {format(new Date(change.submittedAt), 'MMM dd, yyyy')}
                  </span>
                </div>
                <Button variant="ghost" size="sm" onClick={onView}>
                  <Eye className="h-4 w-4 mr-2" />
                  View Details
                </Button>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}